import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { absoluteImageUrl } from '@/lib/imageUrl';
import ShareButton from '@/components/ShareButton';
import { ArrowLeft, MapPin, MessageSquare, GraduationCap, Clock, Calendar, ExternalLink, Star, Mail, Copy, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { PremiumInstructorBadge } from '@/components/PremiumInstructorBadge';
import { SummerFoundingPartnerBadge } from '@/components/SummerFoundingPartnerBadge';
import SEO from '@/components/SEO';

function Stars({ rating }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-slate-300'}`}
        />
      ))}
    </div>
  );
}

export default function PlayerProfilePage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState({ reviews: [], count: 0, average_rating: 0 });
  const [reviewsError, setReviewsError] = useState('');

  useEffect(() => {
    api.get(`/profile/${userId}`).then(({ data }) => { setPlayer(data.profile); setLoading(false); })
      .catch(() => { setLoading(false); });
    api.get(`/reviews/user/${userId}`)
      .then(({ data }) => setReviewData(data))
      .catch(err => setReviewsError(err.response?.data?.detail || ''));
  }, [userId]);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#56dfb4] border-t-transparent rounded-full" /></div>;
  if (!player) return <div className="text-center py-20 text-slate-400">Player not found</div>;

  const photoUrl = absoluteImageUrl(player.profile_photo);
  const locText = [player.city, player.state].filter(Boolean).join(', ');
  const treatAsInstructor = player.is_instructor || player.is_premium_instructor;

  // Schema.org Person for instructors (richer search-result cards),
  // basic Person stub for everyone else. Aggregate review rating is
  // included when count > 0 so Google can render ★ stars. We emit a
  // LocalBusiness sibling when the profile is a public instructor with
  // a known city — that's what powers "near me" / city-scoped queries.
  const personSchema = (() => {
    const base = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: player.display_name,
      url: `https://meetformahjong.com/player/${player.id}`,
    };
    if (photoUrl) base.image = photoUrl;
    if (treatAsInstructor) {
      base.jobTitle = 'American Mahjong Instructor';
      if (player.instructor_bio) base.description = player.instructor_bio;
      const addr = { '@type': 'PostalAddress', addressCountry: 'US' };
      if (player.city) addr.addressLocality = player.city;
      if (player.state) addr.addressRegion = player.state;
      if (player.zip_code) addr.postalCode = player.zip_code;
      base.address = addr;
      if (Array.isArray(player.instructor_lesson_types) && player.instructor_lesson_types.length) {
        base.knowsAbout = player.instructor_lesson_types.map(t => `American Mahjong ${t}`);
      }
    }
    if (reviewData.count > 0) {
      base.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: reviewData.average_rating,
        reviewCount: reviewData.count,
        bestRating: 5,
        worstRating: 1,
      };
    }
    return base;
  })();

  const localBusinessSchema = treatAsInstructor && (player.city || player.zip_code) ? {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `https://meetformahjong.com/player/${player.id}#business`,
    name: `${player.display_name} — American Mahjong Instructor`,
    description: player.instructor_bio || `${player.display_name} teaches American Mahjong${locText ? ` in ${locText}` : ''}.`,
    url: `https://meetformahjong.com/player/${player.id}`,
    image: photoUrl || undefined,
    priceRange: player.instructor_rate || '$$',
    address: {
      '@type': 'PostalAddress',
      addressLocality: player.city || undefined,
      addressRegion: player.state || undefined,
      postalCode: player.zip_code || undefined,
      addressCountry: 'US',
    },
    areaServed: locText || (player.zip_code ? `ZIP ${player.zip_code}` : 'United States'),
    knowsAbout: ['American Mahjong', 'Mah Jongg', 'NMJL', 'Mahjong lessons'],
  } : null;

  // Bundle Person + (optional) LocalBusiness so both surface in SERP.
  const fullJsonLd = localBusinessSchema ? [personSchema, localBusinessSchema] : personSchema;

  // Keyword-rich SEO title — drives long-tail "mahjong instructor near
  // <city>" rankings. Non-instructor profiles use a simpler form so
  // they don't compete for instructor-intent searches.
  const seoTitle = treatAsInstructor
    ? (locText
        ? `American Mahjong Instructor in ${locText}: ${player.display_name}`
        : `${player.display_name} — American Mahjong Instructor`)
    : (locText
        ? `${player.display_name} — American Mahjong Player (${locText})`
        : player.display_name);

  const seoDescription = treatAsInstructor
    ? (player.instructor_bio
        ? `American Mahjong lessons${locText ? ` in ${locText}` : ''} with ${player.display_name}. ${player.instructor_bio}`.slice(0, 280)
        : `${player.display_name} teaches American Mahjong${locText ? ` in ${locText}` : ''}. Group and private lessons, classes, and Mahjong events on Meet For Mahjong.`)
    : `${player.display_name} plays American Mahjong on Meet For Mahjong${locText ? ` (${locText})` : ''}.`;

  return (
    <div data-testid="player-profile-page" className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/player/${player.id}`}
        image={photoUrl || undefined}
        jsonLd={fullJsonLd}
      />
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {/* "Edit my profile" Quick-Edit shortcut — only renders for the
            profile owner. Caught 24 Jun 2026 — new users were emailing
            support to find the bio editor. */}
        {user && (user.user_id === player.id || user.id === player.id) && (
          <Link
            to="/profile"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#56dfb4]/15 text-[#0f172a] hover:bg-[#56dfb4]/30 text-sm font-medium transition-colors"
            data-testid="player-profile-edit-mine-btn"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit my profile
          </Link>
        )}
      </div>

      <Card className="border-slate-100">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            {photoUrl ? (
              <img src={photoUrl} alt={player.display_name} className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#56dfb4]/15 flex items-center justify-center text-3xl font-bold text-[#0f172a]">
                {(player.display_name || 'U')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{player.display_name}</h1>
                {player.is_premium_instructor && <PremiumInstructorBadge size="sm" />}
                {player.is_summer_founding_partner && <SummerFoundingPartnerBadge size="sm" />}
              </div>
              <div className="flex items-center gap-1 text-sm text-slate-500">
                <MapPin className="w-3.5 h-3.5" />
                <span data-testid="profile-location-text">
                  {locText || player.zip_code || 'Location not set'}
                </span>
              </div>
            </div>
          </div>

          {player.bio && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">About</p>
              <p className="text-sm text-slate-600">{player.bio}</p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Mahjong Profile</p>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="secondary" className="text-xs capitalize rounded-full">{player.skill_level}</Badge>
              {player.has_mahjong_set === 'yes' && <Badge className="text-xs bg-[#56dfb4]/15 text-[#0f172a] rounded-full">Has Mahjong Set</Badge>}
              {player.meetup_type === 'public' ? <Badge variant="outline" className="text-xs rounded-full">Public Meetups Only</Badge> : <Badge variant="outline" className="text-xs rounded-full">Public & Private</Badge>}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Availability</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="font-medium text-xs text-slate-600">Days</p>
                  <p className="text-xs text-slate-500">{(player.days_available||[]).map(d=>d.charAt(0).toUpperCase()+d.slice(1,3)).join(', ') || 'Not set'}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="font-medium text-xs text-slate-600">Times</p>
                  <p className="text-xs text-slate-500 capitalize">{(player.time_preferences||[]).join(', ') || 'Not set'}</p>
                </div>
              </div>
            </div>
          </div>

          {player.is_instructor && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Instructor Profile</p>
              <div className="p-3 rounded-xl bg-[#b456df]/5 border border-[#b456df]/20">
                <div className="flex items-center gap-2 mb-1">
                  <GraduationCap className="w-4 h-4 text-[#b456df]" />
                  <span className="text-sm font-medium">Mahjong Instructor</span>
                </div>
                {player.instructor_bio && <p className="text-xs text-slate-600 mt-1">{player.instructor_bio}</p>}
                {player.instructor_rate && <p className="text-xs text-slate-500 mt-1">Rate: {player.instructor_rate}</p>}
                {(player.contact_email || player.email) && (
                  <>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <a
                        href={`mailto:${player.contact_email || player.email}`}
                        className="inline-flex items-center gap-1.5 text-xs text-[#b456df] hover:underline break-all"
                        data-testid="instructor-profile-email"
                      >
                        <Mail className="w-3 h-3 flex-shrink-0" /> {player.contact_email || player.email}
                      </a>
                      <button
                        type="button"
                        onClick={async () => {
                          const addr = player.contact_email || player.email;
                          try {
                            await navigator.clipboard.writeText(addr);
                            toast.success('Email copied');
                          } catch {
                            toast.error('Could not copy — long-press the email to copy');
                          }
                        }}
                        className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#b456df] px-1.5 py-0.5 rounded border border-slate-200 hover:border-[#b456df]/40 transition-colors"
                        data-testid="instructor-profile-email-copy"
                        aria-label="Copy instructor email"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <p
                      className="text-[11px] text-slate-500 italic mt-2 leading-snug"
                      data-testid="instructor-mention-mfm-note"
                    >
                      <span className="not-italic">💬</span> When you reach out, please mention you found {player.display_name?.split(' ')[0] || 'them'} on <span className="font-semibold text-[#b456df] not-italic">Meet For Mahjong</span> — it helps instructors see they're being discovered here.
                    </p>
                  </>
                )}
                {(player.instructor_lesson_types||[]).length > 0 && (
                  <div className="flex gap-1 mt-2">{player.instructor_lesson_types.map(t=><Badge key={t} className="text-xs bg-[#b456df]/10 text-[#b456df] rounded-full capitalize">{t}</Badge>)}</div>
                )}
              </div>
            </div>
          )}

          {(player.social_facebook || player.social_instagram || player.social_twitter || player.social_linkedin) && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Social Media</p>
              <div className="space-y-1.5">
                {player.social_facebook && <a href={player.social_facebook.startsWith('http') ? player.social_facebook : `https://${player.social_facebook}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#56c6df] hover:underline"><ExternalLink className="w-3 h-3" />Facebook</a>}
                {player.social_instagram && <p className="text-xs text-slate-600">Instagram: <span className="text-[#56c6df]">@{player.social_instagram.replace('@','')}</span></p>}
                {player.social_twitter && <p className="text-xs text-slate-600">X/Twitter: <span className="text-[#56c6df]">@{player.social_twitter.replace('@','')}</span></p>}
                {player.social_linkedin && <a href={player.social_linkedin.startsWith('http') ? player.social_linkedin : `https://${player.social_linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#56c6df] hover:underline"><ExternalLink className="w-3 h-3" />LinkedIn</a>}
              </div>
            </div>
          )}

          {user && user.id !== player.id && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate(`/messages?to=${player.id}`)} className="flex-1 rounded-full bg-[#56c6df] text-white hover:bg-[#3eb0c9] gap-2" data-testid="send-message-btn">
                <MessageSquare className="w-4 h-4" /> Send Message
              </Button>
              <ShareButton
                shareApiPath={`/api/profile/${player.id}/share`}
                title={player.is_instructor ? `${player.display_name} — Mahjong Instructor` : player.display_name}
                subtitle={player.is_instructor ? (player.instructor_rate || 'Meet For Mahjong') : `${(player.skill_level || '').toUpperCase()} player`}
                testId="share-profile-btn"
              />
            </div>
          )}
          {!user && (
            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-100" data-testid="anon-profile-cta">
              <p className="text-sm text-slate-500 flex-1 self-center">
                {player.is_instructor
                  ? 'Want to book a lesson or message this instructor?'
                  : 'Want to message this player or see contact info?'}
              </p>
              <div className="flex gap-2">
                <Link to={`/login?next=/player/${player.id}`}>
                  <Button variant="outline" className="rounded-full" data-testid="anon-login-btn">Log in</Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full bg-[#56c6df] text-white hover:bg-[#3eb0c9]" data-testid="anon-signup-btn">Sign up free</Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Crawlable, keyword-rich service-area text for instructors —
          gives Googlebot explicit "American Mahjong in <City>, <State>"
          phrasing without spammy keyword stuffing. */}
      {treatAsInstructor && locText && (
        <Card className="border-slate-100 mt-6" data-testid="instructor-service-area-card">
          <CardContent className="p-6">
            <h2
              className="text-lg font-semibold mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              American Mahjong in {locText}
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              {player.display_name} offers American Mahjong{' '}
              {(player.instructor_lesson_types || []).length > 0
                ? player.instructor_lesson_types.join(' and ')
                : 'lessons'}{' '}
              in <span className="font-medium text-slate-800">{locText}</span>
              {player.zip_code ? ` (zip ${player.zip_code})` : ''}
              {' '}— including private lessons, group classes, and Mahjong meetups for beginners through advanced players. Reach out directly to schedule a session or join an upcoming event.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Upcoming events hosted by this user — real `<Link>` elements
          so Google can crawl event detail pages from the profile and
          users can discover what's next without re-searching. */}
      {Array.isArray(player.upcoming_events) && player.upcoming_events.length > 0 && (
        <Card className="border-slate-100 mt-6" data-testid="profile-upcoming-events-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <h2
                className="text-lg font-semibold"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Upcoming Mahjong Events
              </h2>
              <Link
                to="/events"
                className="text-xs text-[#56c6df] hover:underline"
                data-testid="profile-upcoming-events-browse-all"
              >
                Browse all events →
              </Link>
            </div>
            <ul className="space-y-2">
              {player.upcoming_events.map((ev) => {
                const dateLabel = ev.event_date
                  ? new Date(ev.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '';
                const locEv = [ev.city, ev.state].filter(Boolean).join(', ');
                const typeLabel = ({
                  teaching: 'Class',
                  tournament: 'Tournament',
                  casual: 'Open Play',
                  special: 'Special Event',
                }[ev.event_type] || 'Mahjong Event');
                return (
                  <li key={ev.event_id} className="border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                    <Link
                      to={`/events/${ev.event_id}`}
                      className="block hover:bg-slate-50 -mx-2 px-2 py-1 rounded-md transition-colors"
                      data-testid={`profile-upcoming-event-${ev.event_id}`}
                    >
                      <p className="text-sm font-medium text-slate-800 line-clamp-1">{ev.title}</p>
                      <p className="text-xs text-slate-500">
                        {typeLabel}
                        {ev.is_charity && ' · Charity'}
                        {ev.is_free && ' · Free'}
                        {dateLabel && ` · ${dateLabel}`}
                        {locEv && ` · ${locEv}`}
                      </p>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* SEO-friendly footer link back to the instructor directory.
          Crawler-friendly internal linking — both directions. */}
      {treatAsInstructor && (
        <p className="text-sm text-slate-500 text-center mt-6" data-testid="profile-instructors-directory-link">
          <Link to="/instructors" className="text-[#b456df] hover:underline">
            ← Browse all American Mahjong instructors
          </Link>
        </p>
      )}

      {/* Reviews Section */}
      <Card className="border-slate-100 mt-6" data-testid="player-reviews-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Player Reviews</h3>
            {reviewData.count > 0 && (
              <div className="flex items-center gap-2">
                <Stars rating={reviewData.average_rating} />
                <span className="text-sm text-slate-500">
                  {reviewData.average_rating.toFixed(1)} · {reviewData.count} {reviewData.count === 1 ? 'review' : 'reviews'}
                </span>
              </div>
            )}
          </div>
          {reviewData.aggregate_only && reviewData.count > 0 ? (
            <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-4">
              {reviewData.count} review{reviewData.count === 1 ? '' : 's'} on file, average {reviewData.average_rating.toFixed(1)}/5.
              {' '}
              {user ? (
                <Link to="/subscription" className="text-[#df5681] hover:underline">Upgrade to a paid plan</Link>
              ) : (
                <Link to="/register" className="text-[#df5681] hover:underline">Sign up free</Link>
              )} to read individual reviews.
            </div>
          ) : reviewsError ? (
            <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-4">
              {reviewsError} <Link to="/subscription" className="text-[#df5681] hover:underline">Upgrade to a paid plan</Link> to see player reviews.
            </div>
          ) : reviewData.count === 0 ? (
            <p className="text-sm text-slate-400 italic">No reviews yet. After you meet {player.display_name}, you can confirm the meetup in your conversation and leave a review.</p>
          ) : (
            <div className="space-y-3">
              {reviewData.reviews.map((r, idx) => (
                <div key={r.review_id || `review-${idx}`} className="p-3 rounded-xl border border-slate-100 bg-slate-50/50" data-testid={`review-item-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Stars rating={r.rating} />
                    <span className="text-xs text-slate-400">Anonymous · {new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.comment && <p className="text-sm text-slate-600 whitespace-pre-wrap">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-4 italic">Reviews are anonymous to protect privacy. Admins moderate all content.</p>
        </CardContent>
      </Card>
    </div>
  );
}
